import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { useRef } from "react";
import { Text } from "./text";
import { cn } from "@/lib/utils";

const sizes = {
  xs: "sm:max-w-xs",
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  "5xl": "sm:max-w-5xl",
  full: "max-w-screen",
};

export type DialogSize = keyof typeof sizes;

export function Dialog({
  open,
  onClose,
  size = "lg",
  fullScreen = false,
  className,
  children,
  ...props
}: {
  size?: keyof typeof sizes;
  fullScreen?: boolean;
  className?: string;
  children: React.ReactNode;
} & Omit<Headless.DialogProps, "className">) {
  // Track if mousedown was on scrollbar to prevent dismissing dialog
  const mouseDownOnScrollbarRef = useRef(false);

  return (
    <Headless.Transition appear show={open} {...props}>
      <Headless.Dialog
        onClose={() => {
          if (!mouseDownOnScrollbarRef.current) {
            onClose(false);
          }
          mouseDownOnScrollbarRef.current = false;
        }}
      >
        <Headless.TransitionChild
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 focus:outline-0 sm:px-6 sm:py-8 lg:px-8 lg:py-16" />
        </Headless.TransitionChild>

        <div
          className={cn(
            "fixed inset-0 w-screen",
            fullScreen ? "overflow-hidden" : "overflow-y-auto pt-6 sm:pt-0",
          )}
          onMouseDown={(e) => {
            // Prevent scrollbar clicks from dismissing dialog
            if (e.clientX >= e.currentTarget.clientWidth) {
              mouseDownOnScrollbarRef.current = true;
            }
          }}
        >
          <div
            className={cn(
              fullScreen
                ? "h-full w-full *:h-full"
                : "grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4",
            )}
          >
            <Headless.TransitionChild
              enter="ease-out duration-100"
              enterFrom={
                fullScreen
                  ? "opacity-0"
                  : "opacity-0 translate-y-12 sm:translate-y-0 sm:scale-95"
              }
              enterTo={
                fullScreen
                  ? "opacity-100"
                  : "opacity-100 translate-y-0 sm:scale-100"
              }
              leave="ease-in duration-100"
              leaveFrom={
                fullScreen ? "opacity-100" : "opacity-100 translate-y-0"
              }
              leaveTo={
                fullScreen
                  ? "opacity-0"
                  : "opacity-0 translate-y-12 sm:translate-y-0"
              }
            >
              <Headless.DialogPanel
                className={cn(
                  "px-(--gutter-x) py-(--gutter-y) forced-colors:outline-solid w-full min-w-0 bg-white [--gutter-x:--spacing(4)] [--gutter-y:--spacing(8)] sm:[--gutter-x:--spacing(8)]",
                  fullScreen
                    ? "flex h-full max-w-none flex-col overflow-hidden [--gutter-y:--spacing(6)]"
                    : cn(
                        sizes[size],
                        "row-start-2 rounded-t-3xl shadow-lg ring-1 ring-zinc-950/10 sm:mb-auto sm:rounded-2xl",
                      ),
                  className,
                )}
              >
                {children}
              </Headless.DialogPanel>
            </Headless.TransitionChild>
          </div>
        </div>
      </Headless.Dialog>
    </Headless.Transition>
  );
}

export function DialogTitle({
  className,
  ...props
}: { className?: string } & Omit<Headless.DialogTitleProps, "className">) {
  return (
    <Headless.DialogTitle
      {...props}
      className={clsx(
        className,
        "text-balance text-lg/6 font-semibold capitalize text-zinc-950 sm:text-base/6",
      )}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: { className?: string } & Omit<
  Headless.DescriptionProps<typeof Text>,
  "className"
>) {
  return (
    <Headless.Description
      as={Text}
      {...props}
      className={clsx(className, "mt-2 text-pretty")}
    />
  );
}

export function DialogBody({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return <div {...props} className={cn("mt-6", className)} />;
}

export function DialogActions({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        "mt-8 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto",
      )}
    />
  );
}
